'use client'

import { useCallback, useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { toast } from 'sonner'
import { BudgetHistoryPanel } from '@/components/budget/BudgetHistoryPanel'
import { BudgetHero } from '@/components/budget/BudgetHero'
import { CreditBalanceCard } from '@/components/budget/CreditBalanceCard'
import { UsageCard } from '@/components/budget/UsageCard'
import { PostPayActiveSummary } from '@/components/budget/PostPayActiveSummary'
import { InvoiceHistoryCard, PostPayCard } from '@/components/budget/PostPayRow'
import { StatementsCard } from '@/components/budget/StatementsCard'
import { fmtLimitCurrency } from '@/components/budget/limit-card-utils'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import type { BillingMode, BudgetHeroResponse } from '@/app/api/budget/hero/route'

export function WalletPageClient() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [hero, setHero] = useState<BudgetHeroResponse | null>(null)
  const [walletCents, setWalletCents] = useState<number | null>(null)
  const [billingMode, setBillingMode] = useState<BillingMode>('prepay_credits')
  const [error, setError] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const reload = useCallback(async () => {
    setError(null)
    try {
      const res = await fetch('/api/budget/hero')
      if (!res.ok) throw new Error('Unable to load wallet details.')
      const data = (await res.json()) as BudgetHeroResponse & {
        wallet_balance_cents?: number
        billing_mode?: BillingMode
      }
      setHero(data)
      setWalletCents(data.wallet_balance_cents ?? 0)
      setBillingMode(data.billing_mode ?? 'prepay_credits')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load wallet details.')
      setWalletCents(0)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    const deposit = searchParams.get('deposit')
    const paymentMethod = searchParams.get('payment_method')

    if (deposit === 'success') {
      toast.success('Funds added successfully', { duration: 4000 })
      void reload()
    } else if (deposit === 'cancelled') {
      toast('Deposit cancelled', { duration: 4000 })
    }

    if (paymentMethod === 'updated') {
      void (async () => {
        try {
          await fetch('/api/wallet/payment-method/set-default', { method: 'POST' })
          toast.success('Payment method updated', { duration: 4000 })
          void reload()
        } catch {
          // non-fatal
        }
      })()
    } else if (paymentMethod === 'cancelled') {
      toast('Payment method update cancelled', { duration: 4000 })
    }

    if (deposit || paymentMethod) {
      const timeout = window.setTimeout(() => {
        router.replace('/wallet')
      }, 4000)
      return () => window.clearTimeout(timeout)
    }
  }, [reload, router, searchParams])

  const showCreditBalance =
    billingMode === 'prepay_credits' || billingMode === 'hybrid_invoice_fees_prepay_spend'
  const showActivePostPay =
    billingMode === 'postpay_invoice' || billingMode === 'hybrid_invoice_fees_prepay_spend'

  const headerMeta = hero
    ? [
      walletCents != null ? `${fmtLimitCurrency(walletCents)} balance` : null,
      hero.monthly_cap_cents != null
        ? `${fmtLimitCurrency(hero.monthly_cap_cents)} monthly ad spend limit`
        : null,
      `${fmtLimitCurrency(hero.mtd_spend_cents ?? 0)} spent MTD`,
    ].filter(Boolean).join(' · ')
    : 'Loading wallet...'

  return (
    <div className="bv3-canvas min-h-full">
      <LobbyHeaderMeta>{headerMeta}</LobbyHeaderMeta>

      <div className="bv3-bd">
        <div className="bv3-bdwrap pt-8">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            <BudgetHero onReload={reload} />
            <UsageCard />
          </div>

          {showCreditBalance ? (
            <CreditBalanceCard
              balanceCents={walletCents}
              onReload={reload}
              walletLabel={
                billingMode === 'hybrid_invoice_fees_prepay_spend'
                  ? 'Ad spend wallet'
                  : undefined
              }
              walletSublabel={
                billingMode === 'hybrid_invoice_fees_prepay_spend'
                  ? 'Atrium platform fees billed separately via invoice.'
                  : undefined
              }
            />
          ) : null}

          {billingMode === 'prepay_credits' ? (
            <PostPayCard />
          ) : showActivePostPay ? (
            <>
              <PostPayActiveSummary />
              <InvoiceHistoryCard onOpen={() => setHistoryOpen(true)} />
            </>
          ) : null}
          <StatementsCard />
        </div>
      </div>
      <BudgetHistoryPanel
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        smartSpendEnabled={Boolean(hero?.smart_spend_enabled)}
      />
    </div>
  )
}
